import { InputRule, InputRuleFinder, ExtendedRegExpMatchArray, callOrReturn } from '@tiptap/core';
import { CodeBlock } from '.';
import { NodeType } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';

function tableInputRule(config: {
   find: InputRuleFinder;
   type: NodeType;
   getAttributes?:
      | Record<string, any>
      | ((match: ExtendedRegExpMatchArray) => Record<string, any>)
      | false
      | null;
}) {
   return new InputRule({
      find: config.find,
      handler: ({ state, range, match }) => {
         const attributes = callOrReturn(config.getAttributes, undefined, match) || {};
         const { tr, schema } = state;
         const start = range.from;
         const end = range.to;

         if (match[0]) {
            // Start - 1 to include the \n character at the start of a line
            tr.replaceWith(start - 1, end, config.type.create(attributes, schema.text('{table')));
            // Move 6 characters to the right to place the cursor after '{table'
            tr.setSelection(TextSelection.near(tr.doc.resolve(start + 6)));
         }
      },
   });
}

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      table: {
         /**
          * Set a table
          */
         setTable: (attributes?: { language: string }) => ReturnType;
         /**
          * Toggle a table
          */
         toggleTable: (attributes?: { language: string }) => ReturnType;
      };
   }
}

export const Table = CodeBlock.extend({
   name: 'table',
   content: 'inline*',
   marks: '_',

   addInputRules() {
      return [
         tableInputRule({
            find: /^\{table$/,
            type: this.type,
         }),
      ];
   },

   addCommands() {
      // Don't override the default code block commands
      return {
         setTable:
            attributes =>
            ({ commands }) => {
               return commands.setNode(this.name, attributes);
            },
         toggleTable:
            attributes =>
            ({ commands }) => {
               return commands.toggleNode(this.name, 'paragraph', attributes);
            },
      };
   },

   addKeyboardShortcuts() {
      // Same for the default code block keyboard shortcut (Ctrl/Cmd+Alt+C), but keep the rest
      // This is copied from https://github.com/ueberdosis/tiptap/blob/main/packages/extension-code-block/src/code-block.ts
      return {
         // remove code block when at start of document or code block is empty
         Backspace: () => {
            const { empty, $anchor } = this.editor.state.selection;
            const isAtStart = $anchor.pos === 1;

            if (!empty || $anchor.parent.type.name !== this.name) {
               return false;
            }

            if (isAtStart || !$anchor.parent.textContent.length) {
               return this.editor.commands.clearNodes();
            }

            return false;
         },

         // exit node on triple enter
         Enter: ({ editor }) => {
            if (!this.options.exitOnTripleEnter) {
               return false;
            }

            const { state } = editor;
            const { selection } = state;
            const { $from, empty } = selection;

            if (!empty || $from.parent.type !== this.type) {
               return false;
            }

            const isAtEnd = $from.parentOffset === $from.parent.nodeSize - 2;
            const endsWithDoubleNewline = $from.parent.textContent.endsWith('\n\n');

            if (!isAtEnd || !endsWithDoubleNewline) {
               return false;
            }

            return editor
               .chain()
               .command(({ tr }) => {
                  tr.delete($from.pos - 2, $from.pos);

                  return true;
               })
               .exitCode()
               .run();
         },

         // exit node on arrow down
         ArrowDown: ({ editor }) => {
            if (!this.options.exitOnArrowDown) {
               return false;
            }

            const { state } = editor;
            const { selection, doc } = state;
            const { $from, empty } = selection;

            if (!empty || $from.parent.type !== this.type) {
               return false;
            }

            const isAtEnd = $from.parentOffset === $from.parent.nodeSize - 2;

            if (!isAtEnd) {
               return false;
            }

            const after = $from.after();

            if (after === undefined) {
               return false;
            }

            const nodeAfter = doc.nodeAt(after);

            if (nodeAfter) {
               return false;
            }

            return editor.commands.exitCode();
         },
      };
   },
});
